import React from 'react';
import { useWebsiteData } from '../../../context/WebsiteDataContext';

const DashboardOverview = ({ onSectionChange }) => {
  const {
    services,
    testimonials,
    blogs,
    teamMembers,
    calculators,
    projects,
    loading,
    error
  } = useWebsiteData();

  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const stats = [
    { label: 'Total Services', value: services.length, change: 'Active', changeType: 'neutral' },
    { label: 'Testimonials', value: testimonials.length, change: `${testimonials.filter(t => t.status === 'published').length} Published`, changeType: 'positive' },
    { label: 'Blog Posts', value: blogs.length, change: `${blogs.filter(b => b.status === 'published').length} Published`, changeType: 'positive' },
    { label: 'Team Members', value: teamMembers.length, change: 'Active', changeType: 'neutral' },
    { label: 'Calculators', value: calculators.length, change: 'Tools', changeType: 'neutral' },
    { label: 'Projects', value: projects.length, change: `${projects.filter(p => p.status === 'completed').length} Completed`, changeType: 'positive' }
  ];

  // Derive recent activity from projects and blogs (mock sorting by date for now as we might need meaningful timestamps)
  // For now, we take the last 5 items combined
  const recentActivity = [
    ...projects.slice(0, 3).map(p => ({ type: 'project', action: 'Project Update', item: p.title, time: p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : 'Recently' })),
    ...blogs.slice(0, 2).map(b => ({ type: 'blog', action: 'Blog Update', item: b.title, time: b.publishDate || 'Recently' }))
  ].slice(0, 5);

  const quickActions = [
    { label: 'Add New Service', icon: '➕', action: 'services' },
    { label: 'Create Blog Post', icon: '📝', action: 'blog' },
    { label: 'Add Project', icon: '🏗️', action: 'projects' },
    { label: 'Update Calculator', icon: '🧮', action: 'calculators' }
  ];

  const getActivityIcon = (type) => {
    const icons = {
      service: '🛠️',
      testimonial: '💬',
      blog: '📝',
      calculator: '🧮',
      team: '👥',
      project: '🏗️',
      media: '📁'
    };
    return icons[type] || '📋';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
        <p className="text-gray-600">Welcome to Nirmitee Associates Content Management System</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                  stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
                }`}>
                {stat.change}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {recentActivity.length > 0 ? recentActivity.map((activity, index) => (
              <div 
                key={index} 
                onClick={(e) => {
                  e.preventDefault();
                  if (onSectionChange) onSectionChange(activity.type === 'project' ? 'projects' : activity.type === 'blog' ? 'blog' : 'dashboard');
                }}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                role="button"
                tabIndex={0}
              >
                <span className="text-lg">{getActivityIcon(activity.type)}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {activity.action}: {activity.item}
                  </p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            )) : (
              <p className="text-gray-500 text-sm">No recent activity found.</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action, index) => (
              <button
                key={index}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (onSectionChange) onSectionChange(action.action);
                }}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left cursor-pointer"
              >
                <div className="text-2xl mb-2">{action.icon}</div>
                <p className="text-sm font-medium text-gray-900">{action.label}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-700">CMS {isOnline ? 'Online' : 'Offline'}</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${loading ? 'bg-yellow-500 animate-pulse' : error ? 'bg-red-500' : 'bg-green-500'}`}></div>
            <span className="text-sm text-gray-700">Database {loading ? 'Connecting...' : error ? 'Disconnected' : 'Connected (Firebase)'}</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-700">Cloud Storage {isOnline ? 'Active' : 'Unreachable'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview; 